// @flow

import Store from './Store';
import invariant from '../utils/invariant'
import abstractMethod from '../utils/abstractMethod';
import _ from 'lodash';
import DevelopmentUtils from '../utils/DevelopmentUtils';
import Utils from '../utils/Utils';
import GlobalState from './GlobalState';
import deprecated from '../decorators/deprecated';

// on production build, with uglify, the constructor.name is the same. So we increment an id
// in order to have a unique store._id.
let storeId = 0;

/**
* This is the basic building block of a Flux application. All of your stores
* should extend this class.
*
*   class CounterStore extends ReduceStore<number> {
*     getInitialState(): number {
*       return 1;
*     }
*
*     reduce(state: number, action: Object): number {
*       switch(action.type) {
*         case: 'add':
*           return state + action.value;
*         case: 'double':
*           return state * 2;
*         default:
*           return state;
*       }
*     }
*   }
*
*
*  Inspired by https://github.com/facebook/flux/blob/master/src/stores/FluxReduceStore.js
*  We add some PayMyTable code for legacy and transition.
*/
class ReduceStore<TState> extends Store {

  get _state(): TState {
    return GlobalState.getForId(this._id);
  }

  /**
  * The store can use emitEvent. We save the event and run it after the state is updated.
  */
  _eventsToSend = [];

  constructor(dispatcher: Dispatcher<any>) {
    super(dispatcher);

    this._id = `${this.constructor.name}_${storeId}`;
    storeId++;
    // this._state = this.getInitialState();

    // globalState[this._id] = this._state;
    GlobalState.setForId(this._id, this.getInitialState());

    GlobalState.registerStore(this);
  }

  /**
  * Getter that exposes the entire state of this store. If your state is not
  * immutable you should override this and not expose _state directly.
  */
  getState(): TState {
    return this._state;
  }

  @deprecated
  reset() {
    GlobalState.setForId(this._id, this.getInitialState());
  }

  /**
  * Handle legacy stores that can send multiple events for one action.
  * We save the event to send and send it when the store is update.
  */
  emitEvent(event: string) {
    this._eventsToSend.push(event);
  }

  /**
   * Must be override by old stores that does not handle uniq 'onchange' event yet.
   * In those case, must return true
   */
  isEmitingCustomEvents(): boolean {
    return false;
  }

  /**
  * Constructs the initial state for this store. This is called once during
  * construction of the store.
  */
  getInitialState(): TState {
    return abstractMethod('FluxReduceStore', 'getInitialState');
  }

  /**
  * Used to reduce a stream of actions coming from the dispatcher into a
  * single state object.
  */
  reduce(state: TState, action: Object): TState {
    return abstractMethod('FluxReduceStore', 'reduce');
  }

  /**
  * Checks if two versions of state are the same. You do not need to override
  * this if your state is immutable.
  */
  areEqual(one: TState, two: TState): boolean {
    const isEqual = _.isEqual(one, two);
    return isEqual;
    // return one === two;
  }

  __onGlobalStateChange = () => {
    console.log('__onGlobalStateChange', this._id);
    this.__emitter.emit(this.__changeEvent);
  }

  __invokeOnDispatch(action: Object): void {
    this.__changed = false;

    this._eventsToSend = []; // reset event to send

    // Reduce the stream of incoming actions to state, update when necessary.
    const startingState = this._state;

    let endingState;

    const nextState = _.cloneDeepWith(startingState);

    /**
    * PMT legacy.
    * With our dispatcher, we have an object `action` like the payload on AppDispatcher
    * const payload = {  source, action }
    * instead of the action directly..
    *
    */
    if (_.isUndefined(action.type)) {
      endingState = this.reduce(startingState, action.action, nextState);
    } else {
      endingState = this.reduce(startingState, action, nextState);
    }

    // This means your ending state should never be undefined.
    invariant(
      endingState !== undefined,
      '%s returned undefined from reduce(...), did you forget to return ' +
      'state in the default case? (use null if this was intentional)',
      this.constructor.name,
    );

    if (!this.areEqual(startingState, endingState)) {
      // `__emitChange()` sets `this.__changed` to true and then the actual
      // change will be fired from the emitter at the end of the dispatch, this
      // is required in order to support methods like `hasChanged()`
      this.__emitChange();

      if (_.isUndefined(action.type)) {
        this.updateGlobalState(action.action, endingState);
      } else {
        this.updateGlobalState(action, endingState);
      }
    }

    if (this.__changed) {
      if (!_.isEmpty(this._eventsToSend)) {
        _.each(this._eventsToSend, (event: string) => {
          this.__emitter.emit(event);
        });
      } else if (!this.isEmitingCustomEvents()) {
        this.__emitter.emit(this.__changeEvent);
      }
    } else if (!_.isEmpty(this._eventsToSend)) {
      console.warn(
        `ReduceStore.areEqual maybe not implemented on ${this._id}`,
        this._eventsToSend
      );
      _.each(this._eventsToSend, (event: string) => {
        this.__emitter.emit(event);
      });
    }
  }

  updateGlobalState(action, state) {
    const actionToSendToDevTools = _.cloneDeepWith(action);
    GlobalState.setForId(this._id, state);

    // add store name to know where the action is handle
    actionToSendToDevTools.type = `${action.type} (${this._id})`;

    DevelopmentUtils.sendToDevTools(actionToSendToDevTools, GlobalState.getGlobalState());
  }

}

export default ReduceStore;
